import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../home/selectors'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { IAttachment, IPatient, IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { EKGImportConstants } from '../store/ekgImport.constants'
import { IPatientVitalsList } from '../../vitals/store/vitals.types'
import { getEKGImportSelector } from '../selectors'
import { IEKGEvent, IEKGImportState, IEKGReport, IReportAttachment } from '../store/ekgImport.types'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { IItems, ITimelineState } from '../../timeline/store/timeline.types'
import { setActivityLog } from '../../timeline/store/timeline.actions'
import { saveReportAttachments } from '../store/ekgImport.actions'
import { getDateTime } from '../../../utilities'
import {
  getAddMedicationAdministeredOptionsSelector,
  getAirwayOptionsSelector,
  getCameraAttachmentsOptionsSelector,
  getCardiacOptionsSelector,
  getChildbirthOBOptionsSelector,
  getCriticalCareOptionsSelector,
  getDrainOptionsSelector,
  getHospitalNotifyOptionsSelector,
  getImmobilizationOptionsSelector,
  getInitiateIvOptionsSelector,
  getIntubationOptionsSelector,
  getMedicalConsultOptionsSelector,
  getVitalOptionsSelector,
  getWoundCareOptionsSelector,
} from '../../options/selectors'
import {
  IAddMedicationAdministeredOptionsState,
  IAirwayOptionsState,
  ICameraAttachmentsOptionsState,
  ICardiacOptionsState,
  IDrainOptionsState,
  IHospitalNotifyOptionsState,
  IIntubationOptionsState,
  IInitiateIvOptionsState,
  IChildbirthOBOptionsState,
  IImmobilizationOptionsState,
  IMedicalConsultOptionsState,
  IWoundCareOptionsState,
  IVitalOptionsState,
  ICriticalCareOptionsState,
} from '../../options/store/options.types'
import { ProceduresObject, constructProceduresObject } from '../utils/constructProceduresObject.util'
import { createTimelineItem } from '../utils/createTimelineItem.util'
import { createPatientVitals } from '../utils/createPatientVitals.util'
import { sortEkgTimelineItems } from '../utils/sortEkgTimelineItems.util'
import { getTimesSelector } from '../../times/selectors'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { updateCustomGroupsFromTimeLine } from '../../nemsisData/store/nemsisCustomData.actions'
import { getProcedureStateSelector } from '../../../components/procedure/selectors'
import { StateCustomDataConstants } from '../../nemsisData/store/nemsisCustomData.constants'

export function* saveEkgImportData() {
  const { selectedPowerOnTime, selectedEvents, selectedReports, reportAttachments }: IEKGImportState = yield select(
    getEKGImportSelector
  )
  const { nemsisCodeOptions } = yield select(getProcedureStateSelector)
  const { items: timelineItems }: ITimelineState = yield select(getPatientTimelineSelector)
  const {
    bloodPressureMethodOptions,
    temperatureMethodOptions,
    heartRateMethodOptions,
    etco2TypeOptions,
  }: IVitalOptionsState = yield select(getVitalOptionsSelector)

  const { categoryOptions }: ICameraAttachmentsOptionsState = yield select(getCameraAttachmentsOptionsSelector)
  const { medicationsOptions, doseUnitOptions }: IAddMedicationAdministeredOptionsState = yield select(
    getAddMedicationAdministeredOptionsSelector
  )
  const { actionOptions: airwayOptions }: IAirwayOptionsState = yield select(getAirwayOptionsSelector)
  const {
    actionOptions: cardiacOptions,
    pacingModeOptions,
    defibTypeOptions,
    typeOptions: waveformTypeOptions,
  }: ICardiacOptionsState = yield select(getCardiacOptionsSelector)
  const { actionOptions: drainOptions }: IDrainOptionsState = yield select(getDrainOptionsSelector)
  const { methodOptions: hospitalNotifyOptions }: IHospitalNotifyOptionsState = yield select(
    getHospitalNotifyOptionsSelector
  )
  const { methodOptions: intubationOptions }: IIntubationOptionsState = yield select(getIntubationOptionsSelector)
  const { typeOptions: initiateIvOptions }: IInitiateIvOptionsState = yield select(getInitiateIvOptionsSelector)
  const { actionOptions: childbirthOptions }: IChildbirthOBOptionsState = yield select(getChildbirthOBOptionsSelector)
  const { devicesUsedOptions: immobilizationOptions }: IImmobilizationOptionsState = yield select(
    getImmobilizationOptionsSelector
  )
  const { methodOptions: medicalConsultOptions }: IMedicalConsultOptionsState = yield select(
    getMedicalConsultOptionsSelector
  )
  const { actionsOptions: woundCareOptions }: IWoundCareOptionsState = yield select(getWoundCareOptionsSelector)
  const { actionOptions: criticalCareOptions }: ICriticalCareOptionsState = yield select(getCriticalCareOptionsSelector)
  const { ekgImportConfig }: IConfigurationsState = yield select(getConfigurationsSelector)

  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = yield select(getTimesSelector)

  let atPatientDateTime: Date | null = null
  let onSceneDateTime: Date | null = null
  if (atPatientDate && atPatientTime) atPatientDateTime = new Date(`${atPatientDate} ${atPatientTime}`)
  if (onSceneDate && onSceneTime) onSceneDateTime = new Date(`${onSceneDate} ${onSceneTime}`)

  let ptaCutoff: Date | null = new Date(0)
  if (atPatientDateTime && atPatientDateTime > ptaCutoff) ptaCutoff = atPatientDateTime
  if (onSceneDateTime && onSceneDateTime > ptaCutoff) ptaCutoff = onSceneDateTime
  if (Number(ptaCutoff) === 0) ptaCutoff = null

  const serviceId: string = yield select(getServiceIdSelector)
  const pcrId: string = yield select(getPcrIdSelector)
  if (!pcrId) return

  const newPatientVitals: IPatientVitalsList = []
  const vitalsTimelineItems: IItems = []

  selectedEvents.forEach((vEvent: IEKGEvent) => {
    const alreadyInTimeline = timelineItems.some((tItem) => tItem.key === vEvent.id.toString())

    const eventDate = new Date(vEvent.dtDevice)
    const isPTA = !!ptaCutoff && eventDate < ptaCutoff

    if (!alreadyInTimeline) {
      const timelineItem = createTimelineItem(vEvent.eventVitals, isPTA)
      vitalsTimelineItems.push(timelineItem)

      const patientVitals = createPatientVitals(
        vEvent.eventVitals,
        isPTA,
        bloodPressureMethodOptions,
        temperatureMethodOptions,
        heartRateMethodOptions,
        etco2TypeOptions
      )
      newPatientVitals.push(patientVitals)
    }
  })

  // Create procedures and add them to the timeline
  const procedureEvents = selectedEvents.filter((vEvent: IEKGEvent) => {
    return !!vEvent.procedure
  })

  const initialProceduresObject: ProceduresObject = {
    timelineItems: [],
    addMedicationAdministeredStateList: [],
    airwayStateList: [],
    ventilatorStateList: [],
    cardiacStateList: [],
    drainStateList: [],
    initiateIvStateList: [],
    hospitalNotifyStateList: [],
    intubationStateList: [],
    labsStateList: [],
    titrateStateList: [],
    operationsStateList: [],
    childbirthStateList: [],
    extricationStateList: [],
    immobilizationStateList: [],
    medicalConsultStateList: [],
    rescueStateList: [],
    woundCareStateList: [],
    criticalCareStateList: [],
  }
  let proceduresObject = initialProceduresObject

  if (procedureEvents.length) {
    proceduresObject = constructProceduresObject({
      procedureEvents: procedureEvents,
      procedureOptions: {
        medicationsOptions: medicationsOptions,
        doseUnitOptions: doseUnitOptions,
        airwayOptions: airwayOptions,
        cardiacOptions: cardiacOptions,
        pacingModeOptions: pacingModeOptions,
        defibTypeOptions: defibTypeOptions,
        waveformTypeOptions: waveformTypeOptions,
        drainOptions: drainOptions,
        hospitalNotifyOptions: hospitalNotifyOptions,
        intubationOptions: intubationOptions,
        initiateIvOptions: initiateIvOptions,
        childbirthOptions: childbirthOptions,
        immobilizationOptions: immobilizationOptions,
        medicalConsultOptions: medicalConsultOptions,
        woundCareOptions: woundCareOptions,
        criticalCareOptions: criticalCareOptions,
        nemsisCodeOptions: nemsisCodeOptions,
      },
      proceduresObject: initialProceduresObject,
      nemsisCodeOptions,
      timelineItems,
      ptaCutoff,
    })
  }

  const procedureTimelineItems = proceduresObject.timelineItems
  const sortedTimelineItems = sortEkgTimelineItems(timelineItems, vitalsTimelineItems, procedureTimelineItems)
  yield put(setActivityLog(sortedTimelineItems))

  const newReportAttachments = (selectedReports as Array<IEKGReport>)
    .filter((report) => {
      const alreadyInAttachments = reportAttachments.some((rAttachment) => Number(rAttachment.id) === report.id)
      return !alreadyInAttachments
    })
    .map((report, i) => {
      const ekgCategory = categoryOptions.find((option) => {
        return option.id === ekgImportConfig.ekgAttachmentCategory
      })

      const reportFileAttachment: IReportAttachment = {
        index: i + (reportAttachments?.length || 0),
        fileExtension: report.fileName?.split('.').pop() || '',
        data: report.image?.imageString || '',
        category: ekgCategory,
        name: report.fileName || report.description || '',
        uploadedDt: getDateTime(),
        id: report.id.toString(),
        description: report.description,
        isLoading: false,
        isUnavailable: false,
      }

      return reportFileAttachment
    })

  yield put(saveReportAttachments([...(reportAttachments || []), ...newReportAttachments]))

  try {
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrID: string = yield select(getPcrIdSelector)

    if (pcrID) {
      const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
      const patient = pcrDoc.patient
      const patientVitals = pcrDoc.patient?.patientVitals

      const ekgId = selectedPowerOnTime?.id ? selectedPowerOnTime.id.toString() : ''
      let importedEkgIds: Array<string> = []

      if (!pcrDoc.importedEkgIds) {
        importedEkgIds = [ekgId]
      } else {
        importedEkgIds = [...pcrDoc.importedEkgIds]

        if (!pcrDoc.importedEkgIds.includes(ekgId)) {
          importedEkgIds = [...pcrDoc.importedEkgIds, ekgId]
        }
      }

      const patientData: IPatient = {
        ...patient,
        patientVitals: [...(patientVitals || []), ...newPatientVitals],
        timelineItems: sortedTimelineItems,
        medicationAdministeredStateList: [
          ...(patient.medicationAdministeredStateList || []),
          ...proceduresObject.addMedicationAdministeredStateList,
        ],
        airwayStateList: [...(patient.airwayStateList || []), ...proceduresObject.airwayStateList],
        ventilatorStateList: [...(patient.ventilatorStateList || []), ...proceduresObject.ventilatorStateList],
        cardiacStateList: [...(patient.cardiacStateList || []), ...proceduresObject.cardiacStateList],
        drainStateList: [...(patient.drainStateList || []), ...proceduresObject.drainStateList],
        initiateIvStateList: [...(patient.initiateIvStateList || []), ...proceduresObject.initiateIvStateList],
        hospitalNotifyStateList: [
          ...(patient.hospitalNotifyStateList || []),
          ...proceduresObject.hospitalNotifyStateList,
        ],
        intubationStateList: [...(patient.intubationStateList || []), ...proceduresObject.intubationStateList],
        labsStateList: [...(patient.labsStateList || []), ...proceduresObject.labsStateList],
        titrateStateList: [...(patient.titrateStateList || []), ...proceduresObject.titrateStateList],
        operationsStateList: [...(patient.operationsStateList || []), ...proceduresObject.operationsStateList],
        childbirthOBStateList: [...(patient.childbirthOBStateList || []), ...proceduresObject.childbirthStateList],
        extricationStateList: [...(patient.extricationStateList || []), ...proceduresObject.extricationStateList],
        immobilizationStateList: [
          ...(patient.immobilizationStateList || []),
          ...proceduresObject.immobilizationStateList,
        ],
        medicalConsultStateList: [
          ...(patient.medicalConsultStateList || []),
          ...proceduresObject.medicalConsultStateList,
        ],
        rescueStateList: [...(patient.rescueStateList || []), ...proceduresObject.rescueStateList],
        woundCareStateList: [...(patient.woundCareStateList || []), ...proceduresObject.woundCareStateList],
        criticalCareStateList: [...(patient.criticalCareStateList || []), ...proceduresObject.criticalCareStateList],
      }

      const reportAttachmentsForPcr: IAttachment[] = newReportAttachments.map((reportAttachment) => {
        const { isLoading, isUnavailable, ...attachment } = reportAttachment

        return {
          ...attachment,
          data: '',
        }
      })

      const pcrDocUpdated: IPcr = {
        ...pcrDoc,
        patient: patientData,
        _ekgReportAttachments: [...(pcrDoc._ekgReportAttachments || []), ...reportAttachmentsForPcr],
        importedEkgIds: importedEkgIds,
      }

      try {
        yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
        yield put(updateCustomGroupsFromTimeLine(StateCustomDataConstants.ADD_TIMELINE_GROUP))
      } catch (e) {
        yield put(throwPcrException(`savePatientVitals() error: ${e}`))
      }
    } else {
      // todo: add to audit log
    }
  } catch (e) {
    yield put(throwPcrException(`savePatientVitals() error: ${e}`))
  }
}

export function* saveEkgImportDataSaga() {
  yield takeLatest([EKGImportConstants.SAVE_EKG_IMPORT_DATA], saveEkgImportData)
}
